<template>
    <!-- A modal dialog: it covers the page, so focus moves into it, stays in
         it, leaves on Escape or the close button, and goes back to the bell.
         useDialog does that work. The focus trap that was here never
         switched on: `open.value === true` compared instead of assigning. -->
    <section ref="dialogEl" class="notifications-modal" role="dialog" aria-modal="true" aria-labelledby="notifications-title" tabindex="-1">
        <div class="trap-wrapper">
            <div class="container hd-container">
                <h2 id="notifications-title" class="subhead-2">Notifications</h2>
                <button type="button" class="x-button" aria-label="Close" @click="close">
                    <svg aria-hidden="true" focusable="false" width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="0.63423" y1="15.9827" x2="15.4835" y2="1.13344" stroke="black" stroke-width="1.25" />
                        <line x1="0.441942" y1="1.55806" x2="15.2912" y2="16.4073" stroke="black" stroke-width="1.25" />
                    </svg>
                </button>
            </div>

            <div class="notifications-list">
                <ul>
                    <!-- An announcement has no destination of its own, so the item
                         is content, not a control; links inside the copy are the
                         controls. The click listener only records the open, and
                         it still fires when a keyboard user activates one of
                         those links, since that click bubbles here. -->
                    <li
                        v-for="(item, index) in notifications"
                        :key="'notification-' + index"
                        class="notification-item"
                        :class="{ new: index < notifications.length - lastSeen }"
                        @click="trackNotificationOpened(item)">
                        <small>{{ proccessDateTime(item?.fields['Created']) }}</small>
                        <h3 v-if="item?.fields['Announcement Type']" class="eyebrow">{{ item?.fields['Announcement Type'] }}</h3>
                        <!-- A <div>, not a <p>: the announcement is rich text with
                             its own paragraphs, and a <p> can't contain one. -->
                        <div class="copy" v-if="item?.fields['Announcement']" v-html="item?.fields['Announcement']"></div>
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>

<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';

    // Stores
    import { useUserStore } from '~/stores/user';
    const userStore = useUserStore();

    // Props
    const props = defineProps({
        notifications: {
            type: Array,
            default: [],
        },
    });

    // Variables
    const { notifications } = toRefs(props);
    const { readNotifications } = storeToRefs(userStore);
    const dialogEl = ref(null);
    const lastSeen = ref(0);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'Sept', 'Oct', 'November', 'December'];

    const { $event } = useNuxtApp();

    // Methods
    function close() {
        $event('hideNotifications');
    }

    // Open for as long as it is mounted (the layouts render it with v-if).
    useDialog(dialogEl, { onEscape: close });

    function trackNotificationOpened(item) {
        if (!item?.id) return;
        const { track } = useAnalytics();
        track('notification_opened', { notification_id: item.id });
    }

    function gtmViewNotifications() {
        if (import.meta.client) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'view_notifications',
                last_seen: lastSeen.value,
            });
        }
    }

    const proccessDateTime = (date) => {
        const dateObj = new Date(date);
        const month = months[dateObj.getMonth()];
        const day = dateObj.getDate();
        let hours = dateObj.getHours();
        const ampm = hours >= 12 ? 'pm' : 'am';
        if (hours > 12) {
            hours = hours - 12;
        }

        let minutes = dateObj.getMinutes();
        if (minutes < 10) {
            minutes = '0' + minutes;
        }
        const formattedTime = hours + ':' + minutes + ' ' + ampm;
        return `${month} ${day} at ${formattedTime}`;
    };

    // Lifecycle
    onMounted(() => {
        if (readNotifications.value) {
            lastSeen.value = parseInt(JSON.parse(readNotifications.value));
        } else {
            lastSeen.value = 0;
        }
        gtmViewNotifications();
    });
</script>

<style lang="scss">
    $top-bar-height: 64px;

    .notifications-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 9999;

        // Focus lands on the dialog itself when it opens (it is not a control).
        &:focus {
            outline: none;
        }

        .trap-wrapper > .container {
            background: $gray-light;
            position: sticky;
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: $top-bar-height;
            left: 0;
            width: 100%;
            top: 0;

            &.hd-container {
                padding: 0 16px;
            }
        }

        .notifications-list {
            position: absolute;
            height: calc(100svh - $header_height);
            width: 100%;
            top: $header_height;
            left: 0;
            right: 0;
            overflow: auto;
            z-index: 5;
            background-color: $white;
            transition: transform 0.5s ease;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 100px;
        }

        .notification-item {
            font-size: 16px;
            font-weight: 350;
            line-height: 21px;
            padding: 21px 16px 19px;
            border-bottom: 1px solid #a6dcdc;

            &.new {
                background: $turquoise;
                color: white;

                small {
                    color: white;
                    font-weight: 800;
                }
            }

            .eyebrow {
                font-size: 14px;
                font-weight: 350;
                line-height: 15px;
                letter-spacing: 0.7px;
                text-transform: uppercase;
            }

            small {
                color: $gray-dark;
            }

            h3 {
                margin: 8px 0;
            }

            .copy a {
                font-weight: 800 !important;
            }
        }
    }
</style>
